import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './Settings.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * Settings - Platform settings and configuration for Super Admin
 */
function Settings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('general'); // general, email, payment, security, system, services, healthChecks

    // General Settings
    const [generalSettings, setGeneralSettings] = useState({
        platformName: 'Ecom Tech BD',
        platformEmail: 'admin@ecomtechbd.com',
        supportEmail: 'support@ecomtechbd.com',
        defaultLanguage: 'en',
        defaultCurrency: 'BDT',
        timezone: 'Asia/Dhaka',
        maintenanceMode: false,
        allowNewRegistrations: true,
    });

    // Email Settings
    const [emailSettings, setEmailSettings] = useState({
        smtpHost: '',
        smtpPort: 587,
        smtpUsername: '',
        smtpPassword: '',
        fromEmail: '',
        fromName: '',
        emailVerificationRequired: true,
    });

    // Payment Settings
    const [paymentSettings, setPaymentSettings] = useState({
        bkashEnabled: true,
        bkashMerchantId: '',
        bkashApiKey: '',
        nagadEnabled: false,
        nagadMerchantId: '',
        rocketEnabled: false,
        stripeEnabled: false,
        stripePublicKey: '',
        stripeSecretKey: '',
        currency: 'BDT',
        taxRate: 0,
    });

    // Security Settings
    const [securitySettings, setSecuritySettings] = useState({
        passwordMinLength: 8,
        requireSpecialChar: true,
        requireNumber: true,
        sessionTimeout: 30, // minutes
        maxLoginAttempts: 5,
        twoFactorEnabled: false,
        ipWhitelistEnabled: false,
        ipWhitelist: '',
    });

    // System Settings
    const [systemSettings, setSystemSettings] = useState({
        backupFrequency: 'daily',
        backupRetention: 30, // days
        logLevel: 'info',
        maxUploadSize: 10, // MB
        cacheEnabled: true,
        cdnEnabled: false,
        cdnUrl: '',
    });

    // Services Settings (Failover Configuration)
    const [servicesSettings, setServicesSettings] = useState({
        dnsUrls: ['http://0.0.0.0:3004', 'http://localhost:3004'],
        dnsTimeout: 2000,
        sslUrls: ['http://0.0.0.0:6002', 'http://localhost:6002'],
        sslTimeout: 5000,
        cdnUrls: ['http://0.0.0.0:3003', 'http://localhost:3003'],
        cdnTimeout: 3000,
        maxRetries: 3,
        retryDelay: 500,
    });

    // Health Check Settings
    const [healthCheckSettings, setHealthCheckSettings] = useState({
        environment: 'development',
        backend: {
            dev: 'http://localhost:4002/api/health',
            prod: 'https://api.ecomtechbd.com/api/health'
        },
        dns: {
            dev: 'http://localhost:3004/api/health',
            prod: 'http://51.81.222.239:3004/api/health'
        },
        ssl: {
            dev: 'http://localhost:6002/health',
            prod: 'http://ssl.ecomtechbd.com/health'
        },
        cdn: {
            dev: 'http://localhost:3003/health',
            prod: 'http://cdn.ecomtechbd.com/health'
        },
        ejs: {
            dev: 'http://localhost:3001/health',
            prod: 'https://storefront.ecomtechbd.com/health'
        },
        order: {
            dev: 'http://localhost:5002/health',
            prod: 'https://order.ecomtechbd.com/health'
        },
        healthCheckTimeout: 3000,
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');
            const response = await axios.get(`${BASEURL}superadmin/settings/${userId}`, {
                headers: { token }
            });

            if (response.data.success) {
                const { general, email, payment, security, system, services, healthChecks } = response.data.data;
                if (general) setGeneralSettings(general);
                if (email) setEmailSettings(email);
                if (payment) setPaymentSettings(payment);
                if (security) setSecuritySettings(security);
                if (system) setSystemSettings(system);
                if (services) setServicesSettings(services);
                if (healthChecks) setHealthCheckSettings(healthChecks);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching settings:', err);
            setLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');
            const settingsData = {
                general: activeTab === 'general' ? generalSettings : undefined,
                email: activeTab === 'email' ? emailSettings : undefined,
                payment: activeTab === 'payment' ? paymentSettings : undefined,
                security: activeTab === 'security' ? securitySettings : undefined,
                system: activeTab === 'system' ? systemSettings : undefined,
                services: activeTab === 'services' ? servicesSettings : undefined,
                healthChecks: activeTab === 'healthChecks' ? healthCheckSettings : undefined,
            };

            const response = await axios.post(
                `${BASEURL}superadmin/settings/${userId}`,
                settingsData,
                { headers: { token } }
            );

            if (response.data.success) {
                alert('Settings saved successfully!');
            }
            setSaving(false);
        } catch (err) {
            console.error('Error saving settings:', err);
            alert('Failed to save settings');
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className={styles.loading}>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading settings...
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.headerContent}>
                        <h1>Platform Settings</h1>
                        <p>Configure platform-wide settings and preferences</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'general' ? styles.active : ''}`}
                        onClick={() => setActiveTab('general')}
                    >
                        <i className="fas fa-cog"></i>
                        General
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'email' ? styles.active : ''}`}
                        onClick={() => setActiveTab('email')}
                    >
                        <i className="fas fa-envelope"></i>
                        Email
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'payment' ? styles.active : ''}`}
                        onClick={() => setActiveTab('payment')}
                    >
                        <i className="fas fa-credit-card"></i>
                        Payment
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'security' ? styles.active : ''}`}
                        onClick={() => setActiveTab('security')}
                    >
                        <i className="fas fa-shield-alt"></i>
                        Security
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'system' ? styles.active : ''}`}
                        onClick={() => setActiveTab('system')}
                    >
                        <i className="fas fa-server"></i>
                        System
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'services' ? styles.active : ''}`}
                        onClick={() => setActiveTab('services')}
                    >
                        <i className="fas fa-network-wired"></i>
                        Services
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'healthChecks' ? styles.active : ''}`}
                        onClick={() => setActiveTab('healthChecks')}
                    >
                        <i className="fas fa-heartbeat"></i>
                        Health Checks
                    </button>
                </div>

                {/* Content */}
                <div className={styles.content}>
                    {/* General Settings */}
                    {activeTab === 'general' && (
                        <div className={styles.section}>
                            <h2>General Settings</h2>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>Platform Name</label>
                                    <input
                                        type="text"
                                        value={generalSettings.platformName}
                                        onChange={(e) =>
                                            setGeneralSettings({ ...generalSettings, platformName: e.target.value })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Platform Email</label>
                                    <input
                                        type="email"
                                        value={generalSettings.platformEmail}
                                        onChange={(e) =>
                                            setGeneralSettings({ ...generalSettings, platformEmail: e.target.value })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Support Email</label>
                                    <input
                                        type="email"
                                        value={generalSettings.supportEmail}
                                        onChange={(e) =>
                                            setGeneralSettings({ ...generalSettings, supportEmail: e.target.value })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Default Language</label>
                                    <select
                                        value={generalSettings.defaultLanguage}
                                        onChange={(e) =>
                                            setGeneralSettings({ ...generalSettings, defaultLanguage: e.target.value })
                                        }
                                    >
                                        <option value="en">English</option>
                                        <option value="bn">বাংলা (Bengali)</option>
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Default Currency</label>
                                    <select
                                        value={generalSettings.defaultCurrency}
                                        onChange={(e) =>
                                            setGeneralSettings({ ...generalSettings, defaultCurrency: e.target.value })
                                        }
                                    >
                                        <option value="BDT">BDT (৳)</option>
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Timezone</label>
                                    <select
                                        value={generalSettings.timezone}
                                        onChange={(e) =>
                                            setGeneralSettings({ ...generalSettings, timezone: e.target.value })
                                        }
                                    >
                                        <option value="Asia/Dhaka">Asia/Dhaka (UTC+6)</option>
                                        <option value="UTC">UTC</option>
                                        <option value="America/New_York">America/New_York</option>
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={generalSettings.maintenanceMode}
                                            onChange={(e) =>
                                                setGeneralSettings({
                                                    ...generalSettings,
                                                    maintenanceMode: e.target.checked,
                                                })
                                            }
                                        />
                                        <span>Maintenance Mode</span>
                                    </label>
                                    <small>Disable access to platform for regular users</small>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={generalSettings.allowNewRegistrations}
                                            onChange={(e) =>
                                                setGeneralSettings({
                                                    ...generalSettings,
                                                    allowNewRegistrations: e.target.checked,
                                                })
                                            }
                                        />
                                        <span>Allow New Registrations</span>
                                    </label>
                                    <small>Enable or disable new user signups</small>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Email Settings */}
                    {activeTab === 'email' && (
                        <div className={styles.section}>
                            <h2>Email Settings</h2>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>SMTP Host</label>
                                    <input
                                        type="text"
                                        value={emailSettings.smtpHost}
                                        onChange={(e) =>
                                            setEmailSettings({ ...emailSettings, smtpHost: e.target.value })
                                        }
                                        placeholder="smtp.gmail.com"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>SMTP Port</label>
                                    <input
                                        type="number"
                                        value={emailSettings.smtpPort}
                                        onChange={(e) =>
                                            setEmailSettings({ ...emailSettings, smtpPort: parseInt(e.target.value) })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>SMTP Username</label>
                                    <input
                                        type="text"
                                        value={emailSettings.smtpUsername}
                                        onChange={(e) =>
                                            setEmailSettings({ ...emailSettings, smtpUsername: e.target.value })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>SMTP Password</label>
                                    <input
                                        type="password"
                                        value={emailSettings.smtpPassword}
                                        onChange={(e) =>
                                            setEmailSettings({ ...emailSettings, smtpPassword: e.target.value })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>From Email</label>
                                    <input
                                        type="email"
                                        value={emailSettings.fromEmail}
                                        onChange={(e) =>
                                            setEmailSettings({ ...emailSettings, fromEmail: e.target.value })
                                        }
                                        placeholder="noreply@ecomtechbd.com"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>From Name</label>
                                    <input
                                        type="text"
                                        value={emailSettings.fromName}
                                        onChange={(e) =>
                                            setEmailSettings({ ...emailSettings, fromName: e.target.value })
                                        }
                                        placeholder="Ecom Tech BD"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={emailSettings.emailVerificationRequired}
                                            onChange={(e) =>
                                                setEmailSettings({
                                                    ...emailSettings,
                                                    emailVerificationRequired: e.target.checked,
                                                })
                                            }
                                        />
                                        <span>Require Email Verification</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Payment Settings */}
                    {activeTab === 'payment' && (
                        <div className={styles.section}>
                            <h2>Payment Gateway Settings</h2>

                            <h3>bKash</h3>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={paymentSettings.bkashEnabled}
                                            onChange={(e) =>
                                                setPaymentSettings({ ...paymentSettings, bkashEnabled: e.target.checked })
                                            }
                                        />
                                        <span>Enable bKash</span>
                                    </label>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>bKash Merchant ID</label>
                                    <input
                                        type="text"
                                        value={paymentSettings.bkashMerchantId}
                                        onChange={(e) =>
                                            setPaymentSettings({ ...paymentSettings, bkashMerchantId: e.target.value })
                                        }
                                        disabled={!paymentSettings.bkashEnabled}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>bKash API Key</label>
                                    <input
                                        type="password"
                                        value={paymentSettings.bkashApiKey}
                                        onChange={(e) =>
                                            setPaymentSettings({ ...paymentSettings, bkashApiKey: e.target.value })
                                        }
                                        disabled={!paymentSettings.bkashEnabled}
                                    />
                                </div>
                            </div>

                            <h3>Other Gateways</h3>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={paymentSettings.nagadEnabled}
                                            onChange={(e) =>
                                                setPaymentSettings({ ...paymentSettings, nagadEnabled: e.target.checked })
                                            }
                                        />
                                        <span>Enable Nagad</span>
                                    </label>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={paymentSettings.rocketEnabled}
                                            onChange={(e) =>
                                                setPaymentSettings({ ...paymentSettings, rocketEnabled: e.target.checked })
                                            }
                                        />
                                        <span>Enable Rocket</span>
                                    </label>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={paymentSettings.stripeEnabled}
                                            onChange={(e) =>
                                                setPaymentSettings({ ...paymentSettings, stripeEnabled: e.target.checked })
                                            }
                                        />
                                        <span>Enable Stripe</span>
                                    </label>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Tax Rate (%)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={paymentSettings.taxRate}
                                        onChange={(e) =>
                                            setPaymentSettings({ ...paymentSettings, taxRate: parseFloat(e.target.value) })
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Security Settings */}
                    {activeTab === 'security' && (
                        <div className={styles.section}>
                            <h2>Security Settings</h2>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>Password Min Length</label>
                                    <input
                                        type="number"
                                        value={securitySettings.passwordMinLength}
                                        onChange={(e) =>
                                            setSecuritySettings({
                                                ...securitySettings,
                                                passwordMinLength: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Session Timeout (minutes)</label>
                                    <input
                                        type="number"
                                        value={securitySettings.sessionTimeout}
                                        onChange={(e) =>
                                            setSecuritySettings({
                                                ...securitySettings,
                                                sessionTimeout: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Max Login Attempts</label>
                                    <input
                                        type="number"
                                        value={securitySettings.maxLoginAttempts}
                                        onChange={(e) =>
                                            setSecuritySettings({
                                                ...securitySettings,
                                                maxLoginAttempts: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={securitySettings.requireSpecialChar}
                                            onChange={(e) =>
                                                setSecuritySettings({
                                                    ...securitySettings,
                                                    requireSpecialChar: e.target.checked,
                                                })
                                            }
                                        />
                                        <span>Require Special Character in Password</span>
                                    </label>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={securitySettings.requireNumber}
                                            onChange={(e) =>
                                                setSecuritySettings({
                                                    ...securitySettings,
                                                    requireNumber: e.target.checked,
                                                })
                                            }
                                        />
                                        <span>Require Number in Password</span>
                                    </label>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={securitySettings.twoFactorEnabled}
                                            onChange={(e) =>
                                                setSecuritySettings({
                                                    ...securitySettings,
                                                    twoFactorEnabled: e.target.checked,
                                                })
                                            }
                                        />
                                        <span>Enable Two-Factor Authentication</span>
                                    </label>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={securitySettings.ipWhitelistEnabled}
                                            onChange={(e) =>
                                                setSecuritySettings({
                                                    ...securitySettings,
                                                    ipWhitelistEnabled: e.target.checked,
                                                })
                                            }
                                        />
                                        <span>Enable IP Whitelist</span>
                                    </label>
                                </div>

                                {securitySettings.ipWhitelistEnabled && (
                                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                        <label>IP Whitelist (one per line)</label>
                                        <textarea
                                            rows="5"
                                            value={securitySettings.ipWhitelist}
                                            onChange={(e) =>
                                                setSecuritySettings({ ...securitySettings, ipWhitelist: e.target.value })
                                            }
                                            placeholder="192.168.1.1&#10;10.0.0.1"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* System Settings */}
                    {activeTab === 'system' && (
                        <div className={styles.section}>
                            <h2>System Settings</h2>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>Backup Frequency</label>
                                    <select
                                        value={systemSettings.backupFrequency}
                                        onChange={(e) =>
                                            setSystemSettings({ ...systemSettings, backupFrequency: e.target.value })
                                        }
                                    >
                                        <option value="hourly">Hourly</option>
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Backup Retention (days)</label>
                                    <input
                                        type="number"
                                        value={systemSettings.backupRetention}
                                        onChange={(e) =>
                                            setSystemSettings({
                                                ...systemSettings,
                                                backupRetention: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Log Level</label>
                                    <select
                                        value={systemSettings.logLevel}
                                        onChange={(e) =>
                                            setSystemSettings({ ...systemSettings, logLevel: e.target.value })
                                        }
                                    >
                                        <option value="error">Error Only</option>
                                        <option value="warn">Warnings</option>
                                        <option value="info">Info</option>
                                        <option value="debug">Debug</option>
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Max Upload Size (MB)</label>
                                    <input
                                        type="number"
                                        value={systemSettings.maxUploadSize}
                                        onChange={(e) =>
                                            setSystemSettings({
                                                ...systemSettings,
                                                maxUploadSize: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={systemSettings.cacheEnabled}
                                            onChange={(e) =>
                                                setSystemSettings({ ...systemSettings, cacheEnabled: e.target.checked })
                                            }
                                        />
                                        <span>Enable Cache</span>
                                    </label>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={systemSettings.cdnEnabled}
                                            onChange={(e) =>
                                                setSystemSettings({ ...systemSettings, cdnEnabled: e.target.checked })
                                            }
                                        />
                                        <span>Enable CDN</span>
                                    </label>
                                </div>

                                {systemSettings.cdnEnabled && (
                                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                        <label>CDN URL</label>
                                        <input
                                            type="text"
                                            value={systemSettings.cdnUrl}
                                            onChange={(e) =>
                                                setSystemSettings({ ...systemSettings, cdnUrl: e.target.value })
                                            }
                                            placeholder="https://cdn.example.com"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Services Settings */}
                    {activeTab === 'services' && (
                        <div className={styles.section}>
                            <h2>Service Failover Configuration</h2>
                            <p className={styles.sectionDescription}>
                                Configure multiple URLs for DNS, SSL, and CDN services to enable automatic failover and high availability.
                                The first URL is the primary, subsequent URLs are backups.
                            </p>

                            {/* DNS Service */}
                            <h3>Legacy DNS reload URLs (EtechDns)</h3>
                            <p className={styles.sectionDescription}>
                                DNS records are published directly to CustomDNS (ns1/ns2.custom-dns.com) using
                                CUSTOMDNSURL and CUSTOMDNSAPI from the backend .env. The URLs below are kept for
                                the old EtechDns server only and are no longer used for DNS writes.
                            </p>
                            <div className={styles.formGrid}>
                                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                    <label>DNS Service URLs</label>
                                    {servicesSettings.dnsUrls.map((url, index) => (
                                        <div key={index} className={styles.urlRow}>
                                            <span className={styles.urlLabel}>
                                                {index === 0 ? 'Primary' : `Backup ${index}`}
                                            </span>
                                            <input
                                                type="text"
                                                value={url}
                                                onChange={(e) => {
                                                    const newUrls = [...servicesSettings.dnsUrls];
                                                    newUrls[index] = e.target.value;
                                                    setServicesSettings({ ...servicesSettings, dnsUrls: newUrls });
                                                }}
                                                placeholder="http://0.0.0.0:3004"
                                            />
                                            {servicesSettings.dnsUrls.length > 1 && (
                                                <button
                                                    type="button"
                                                    className={styles.removeBtn}
                                                    onClick={() => {
                                                        const newUrls = servicesSettings.dnsUrls.filter((_, i) => i !== index);
                                                        setServicesSettings({ ...servicesSettings, dnsUrls: newUrls });
                                                    }}
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className={styles.addBtn}
                                        onClick={() => {
                                            setServicesSettings({
                                                ...servicesSettings,
                                                dnsUrls: [...servicesSettings.dnsUrls, 'http://'],
                                            });
                                        }}
                                    >
                                        <i className="fas fa-plus"></i>
                                        Add Backup URL
                                    </button>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>DNS Timeout (ms)</label>
                                    <input
                                        type="number"
                                        value={servicesSettings.dnsTimeout}
                                        onChange={(e) =>
                                            setServicesSettings({
                                                ...servicesSettings,
                                                dnsTimeout: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                    <small>Request timeout in milliseconds (default: 2000)</small>
                                </div>
                            </div>

                            {/* SSL/Nginx Service */}
                            <h3>SSL/Nginx Service (EtechStaticNginxSSL)</h3>
                            <div className={styles.formGrid}>
                                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                    <label>SSL Service URLs</label>
                                    {servicesSettings.sslUrls.map((url, index) => (
                                        <div key={index} className={styles.urlRow}>
                                            <span className={styles.urlLabel}>
                                                {index === 0 ? 'Primary' : `Backup ${index}`}
                                            </span>
                                            <input
                                                type="text"
                                                value={url}
                                                onChange={(e) => {
                                                    const newUrls = [...servicesSettings.sslUrls];
                                                    newUrls[index] = e.target.value;
                                                    setServicesSettings({ ...servicesSettings, sslUrls: newUrls });
                                                }}
                                                placeholder="http://0.0.0.0:6002"
                                            />
                                            {servicesSettings.sslUrls.length > 1 && (
                                                <button
                                                    type="button"
                                                    className={styles.removeBtn}
                                                    onClick={() => {
                                                        const newUrls = servicesSettings.sslUrls.filter((_, i) => i !== index);
                                                        setServicesSettings({ ...servicesSettings, sslUrls: newUrls });
                                                    }}
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className={styles.addBtn}
                                        onClick={() => {
                                            setServicesSettings({
                                                ...servicesSettings,
                                                sslUrls: [...servicesSettings.sslUrls, 'http://'],
                                            });
                                        }}
                                    >
                                        <i className="fas fa-plus"></i>
                                        Add Backup URL
                                    </button>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>SSL Timeout (ms)</label>
                                    <input
                                        type="number"
                                        value={servicesSettings.sslTimeout}
                                        onChange={(e) =>
                                            setServicesSettings({
                                                ...servicesSettings,
                                                sslTimeout: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                    <small>Request timeout in milliseconds (default: 5000)</small>
                                </div>
                            </div>

                            {/* CDN Service */}
                            <h3>CDN Service (EtechEcomCDN)</h3>
                            <div className={styles.formGrid}>
                                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                    <label>CDN Service URLs</label>
                                    {servicesSettings.cdnUrls.map((url, index) => (
                                        <div key={index} className={styles.urlRow}>
                                            <span className={styles.urlLabel}>
                                                {index === 0 ? 'Primary' : `Backup ${index}`}
                                            </span>
                                            <input
                                                type="text"
                                                value={url}
                                                onChange={(e) => {
                                                    const newUrls = [...servicesSettings.cdnUrls];
                                                    newUrls[index] = e.target.value;
                                                    setServicesSettings({ ...servicesSettings, cdnUrls: newUrls });
                                                }}
                                                placeholder="http://0.0.0.0:3003"
                                            />
                                            {servicesSettings.cdnUrls.length > 1 && (
                                                <button
                                                    type="button"
                                                    className={styles.removeBtn}
                                                    onClick={() => {
                                                        const newUrls = servicesSettings.cdnUrls.filter((_, i) => i !== index);
                                                        setServicesSettings({ ...servicesSettings, cdnUrls: newUrls });
                                                    }}
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className={styles.addBtn}
                                        onClick={() => {
                                            setServicesSettings({
                                                ...servicesSettings,
                                                cdnUrls: [...servicesSettings.cdnUrls, 'http://'],
                                            });
                                        }}
                                    >
                                        <i className="fas fa-plus"></i>
                                        Add Backup URL
                                    </button>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>CDN Timeout (ms)</label>
                                    <input
                                        type="number"
                                        value={servicesSettings.cdnTimeout}
                                        onChange={(e) =>
                                            setServicesSettings({
                                                ...servicesSettings,
                                                cdnTimeout: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                    <small>Request timeout in milliseconds (default: 3000)</small>
                                </div>
                            </div>

                            {/* Failover Settings */}
                            <h3>Failover Settings</h3>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>Max Retries</label>
                                    <input
                                        type="number"
                                        value={servicesSettings.maxRetries}
                                        onChange={(e) =>
                                            setServicesSettings({
                                                ...servicesSettings,
                                                maxRetries: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                    <small>Maximum retry attempts per URL (default: 3)</small>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Retry Delay (ms)</label>
                                    <input
                                        type="number"
                                        value={servicesSettings.retryDelay}
                                        onChange={(e) =>
                                            setServicesSettings({
                                                ...servicesSettings,
                                                retryDelay: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                    <small>Delay between retry attempts (default: 500)</small>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Health Check Settings */}
                    {activeTab === 'healthChecks' && (
                        <div className={styles.section}>
                            <h2>Health Check Configuration</h2>
                            <p className={styles.sectionDescription}>
                                Configure health check URLs for monitoring service availability. These URLs are used by the monitoring system to check if services are running properly.
                            </p>

                            {/* Environment Toggle */}
                            <div className={styles.formGroup} style={{ marginBottom: '30px' }}>
                                <label style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'block' }}>
                                    Active Environment
                                </label>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setHealthCheckSettings({ ...healthCheckSettings, environment: 'development' })}
                                        style={{
                                            flex: 1,
                                            padding: '12px 24px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            border: '2px solid',
                                            borderColor: healthCheckSettings.environment === 'development' ? '#008060' : '#d1d5db',
                                            borderRadius: '8px',
                                            background: healthCheckSettings.environment === 'development' ? '#008060' : 'white',
                                            color: healthCheckSettings.environment === 'development' ? 'white' : '#202223',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        Development
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setHealthCheckSettings({ ...healthCheckSettings, environment: 'production' })}
                                        style={{
                                            flex: 1,
                                            padding: '12px 24px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            border: '2px solid',
                                            borderColor: healthCheckSettings.environment === 'production' ? '#008060' : '#d1d5db',
                                            borderRadius: '8px',
                                            background: healthCheckSettings.environment === 'production' ? '#008060' : 'white',
                                            color: healthCheckSettings.environment === 'production' ? 'white' : '#202223',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        Production
                                    </button>
                                </div>
                                <small>Select which environment URLs to use for health checks</small>
                            </div>

                            {/* Backend Health URLs */}
                            <div style={{ marginBottom: '24px', padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#202223', marginBottom: '16px', marginTop: 0 }}>
                                    Backend Health URL
                                </h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Backend Health URL</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.backend?.dev || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    backend: { ...healthCheckSettings.backend, dev: e.target.value }
                                                })
                                            }
                                            placeholder="http://localhost:4002/api/health"
                                        />
                                        <small>Main backend API health check endpoint</small>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>Backend Health URL (Production)</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.backend?.prod || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    backend: { ...healthCheckSettings.backend, prod: e.target.value }
                                                })
                                            }
                                            placeholder="https://api.ecomtechbd.com/api/health"
                                        />
                                        <small>Production backend health endpoint</small>
                                    </div>
                                </div>
                            </div>

                            {/* DNS Service Health URLs */}
                            <div style={{ marginBottom: '24px', padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#202223', marginBottom: '16px', marginTop: 0 }}>
                                    DNS Service Health URL
                                </h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>DNS Service Health URL</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.dns?.dev || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    dns: { ...healthCheckSettings.dns, dev: e.target.value }
                                                })
                                            }
                                            placeholder="https://custom-dns.com/api/health"
                                        />
                                        <small>DNS service (CustomDNS) health check endpoint</small>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>DNS Service Health URL (Production)</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.dns?.prod || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    dns: { ...healthCheckSettings.dns, prod: e.target.value }
                                                })
                                            }
                                            placeholder="http://51.81.222.239:3004/api/health"
                                        />
                                        <small>Production DNS health endpoint</small>
                                    </div>
                                </div>
                            </div>

                            {/* SSL/Nginx Service Health URLs */}
                            <div style={{ marginBottom: '24px', padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#202223', marginBottom: '16px', marginTop: 0 }}>
                                    SSL/Nginx Service Health URL
                                </h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>SSL/Nginx Service Health URL</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.ssl?.dev || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    ssl: { ...healthCheckSettings.ssl, dev: e.target.value }
                                                })
                                            }
                                            placeholder="http://localhost:6002/health"
                                        />
                                        <small>SSL/Nginx service (EtechStaticNginxSSL) health check endpoint</small>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>SSL/Nginx Service Health URL (Production)</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.ssl?.prod || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    ssl: { ...healthCheckSettings.ssl, prod: e.target.value }
                                                })
                                            }
                                            placeholder="http://ssl.ecomtechbd.com/health"
                                        />
                                        <small>Production SSL health endpoint</small>
                                    </div>
                                </div>
                            </div>

                            {/* CDN Service Health URLs */}
                            <div style={{ marginBottom: '24px', padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#202223', marginBottom: '16px', marginTop: 0 }}>
                                    CDN Service Health URL
                                </h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>CDN Service Health URL</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.cdn?.dev || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    cdn: { ...healthCheckSettings.cdn, dev: e.target.value }
                                                })
                                            }
                                            placeholder="http://localhost:3003/health"
                                        />
                                        <small>CDN service (EtechEcomCDN) health check endpoint</small>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>CDN Service Health URL (Production)</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.cdn?.prod || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    cdn: { ...healthCheckSettings.cdn, prod: e.target.value }
                                                })
                                            }
                                            placeholder="http://cdn.ecomtechbd.com/health"
                                        />
                                        <small>Production CDN health endpoint</small>
                                    </div>
                                </div>
                            </div>

                            {/* Nginx Manager Health URLs */}
                            <div style={{ marginBottom: '24px', padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#202223', marginBottom: '16px', marginTop: 0 }}>
                                    Nginx Manager Health URL
                                </h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Nginx Manager Health URL</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.nginxManager?.dev || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    nginxManager: { ...healthCheckSettings.nginxManager, dev: e.target.value }
                                                })
                                            }
                                            placeholder="http://localhost:3060/api/health"
                                        />
                                        <small>Nginx configuration manager health check endpoint</small>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>Nginx Manager Health URL (Production)</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.nginxManager?.prod || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    nginxManager: { ...healthCheckSettings.nginxManager, prod: e.target.value }
                                                })
                                            }
                                            placeholder="https://nginx-m.ecomtechbd.com/api/health"
                                        />
                                        <small>Production Nginx Manager health endpoint</small>
                                    </div>
                                </div>
                            </div>

                            {/* Order Service Health URLs */}
                            <div style={{ marginBottom: '24px', padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#202223', marginBottom: '16px', marginTop: 0 }}>
                                    Order Service Health URL
                                </h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Order Service Health URL</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.order?.dev || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    order: { ...healthCheckSettings.order, dev: e.target.value }
                                                })
                                            }
                                            placeholder="http://localhost:5002/health"
                                        />
                                        <small>Order processing service health check endpoint</small>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>Order Service Health URL (Production)</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.order?.prod || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    order: { ...healthCheckSettings.order, prod: e.target.value }
                                                })
                                            }
                                            placeholder="https://order.ecomtechbd.com/health"
                                        />
                                        <small>Production order health endpoint</small>
                                    </div>
                                </div>
                            </div>

                            {/* ISP Manager Health URLs */}
                            <div style={{ marginBottom: '24px', padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#202223', marginBottom: '16px', marginTop: 0 }}>
                                    ISP Manager Health URL
                                </h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>ISP Manager Health URL</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.ispManager?.dev || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    ispManager: { ...healthCheckSettings.ispManager, dev: e.target.value }
                                                })
                                            }
                                            placeholder="http://localhost:3050/api/health"
                                        />
                                        <small>ISP network management service health check endpoint</small>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>ISP Manager Health URL (Production)</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.ispManager?.prod || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    ispManager: { ...healthCheckSettings.ispManager, prod: e.target.value }
                                                })
                                            }
                                            placeholder="https://isp-manager.ecomtechbd.com/api/health"
                                        />
                                        <small>Production ISP Manager health endpoint</small>
                                    </div>
                                </div>
                            </div>

                            {/* Frontend/SuperAdmin Health URLs */}
                            <div style={{ marginBottom: '24px', padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#202223', marginBottom: '16px', marginTop: 0 }}>
                                    Root Website (Public Site) Health URL
                                </h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Root Website Health URL</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.frontend?.dev || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    frontend: { ...healthCheckSettings.frontend, dev: e.target.value }
                                                })
                                            }
                                            placeholder="http://localhost:3000/"
                                        />
                                        <small>Root/public website (ecomtechbd.com) health check endpoint</small>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>Root Website Health URL (Production)</label>
                                        <input
                                            type="text"
                                            value={healthCheckSettings.frontend?.prod || ''}
                                            onChange={(e) =>
                                                setHealthCheckSettings({
                                                    ...healthCheckSettings,
                                                    frontend: { ...healthCheckSettings.frontend, prod: e.target.value }
                                                })
                                            }
                                            placeholder="https://ecomtechbd.com/"
                                        />
                                        <small>Production root website health endpoint</small>
                                    </div>
                                </div>
                            </div>

                            {/* Health Check Timeout */}
                            <div style={{ padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#202223', marginBottom: '16px', marginTop: 0 }}>
                                    Health Check Timeout (ms)
                                </h3>
                                <div className={styles.formGroup} style={{ maxWidth: '400px' }}>
                                    <input
                                        type="number"
                                        value={healthCheckSettings.healthCheckTimeout}
                                        onChange={(e) =>
                                            setHealthCheckSettings({
                                                ...healthCheckSettings,
                                                healthCheckTimeout: parseInt(e.target.value),
                                            })
                                        }
                                    />
                                    <small>Maximum time to wait for health check response (default: 3000ms)</small>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Save Button */}
                    <div className={styles.actions}>
                        <button className={styles.saveBtn} onClick={handleSaveSettings} disabled={saving}>
                            {saving ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-save"></i>
                                    Save Settings
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default Settings;
